import { useSidebar } from "../context/SidebarContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useMobile from "../hooks/useMobile";
import { useUser } from "../context/UserContext";
import { useNotification } from "../context/NotificationContext";
import { useEffect, useRef, useState } from "react";
import NotificationDrawer from "./NotificationDrawer";
import { searchItems, ISearchItem } from "./searchData";
import {
  fetchSearchResults,
  formatSearchResult,
  FormattedSearchResult,
} from "../api/SearchApi";

const SearchHeader = () => {
  const { setSidebarOpen } = useSidebar();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { user } = useUser();
  const { unreadCount } = useNotification();
  const location = useLocation();

  const isDashboard = location.pathname === "/";
  const [showNotification, setShowNotification] = useState(false);

  const [search, setSearch] = useState("");
  const [staticResults, setStaticResults] = useState<ISearchItem[]>([]);
  const [apiResults, setApiResults] = useState<FormattedSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);

  // Scroll to section if specified in location state
  useEffect(() => {
    const sectionId = location.state?.sectionId;

    if (!sectionId) return;

    const timer = setTimeout(() => {
      const element = document.getElementById(sectionId);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        navigate(location.pathname, {
          replace: true,
          state: {},
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [location, navigate]);

  // Handle Search Input Change & Backend API Querying with Debounce
  useEffect(() => {
    const trimmedQuery = search.trim();

    if (!trimmedQuery) {
      setStaticResults([]);
      setApiResults([]);
      setIsLoading(false);
      return;
    }

    // Filter static menu/section items
    const filteredStatic = searchItems.filter(
      (item) =>
        item.title.toLowerCase().includes(trimmedQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(trimmedQuery.toLowerCase()),
    );
    setStaticResults(filteredStatic);

    // If query is less than 2 characters, don't trigger backend search
    if (trimmedQuery.length < 2) {
      setApiResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const handler = setTimeout(async () => {
      try {
        const rawResults = await fetchSearchResults(trimmedQuery);
        const formatted = rawResults.map(formatSearchResult);
        setApiResults(formatted);
      } catch (err) {
        console.error("Backend search failed:", err);
        setApiResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [search]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getBadgeClass = (category: string) => {
    switch (category?.toLowerCase()) {
      case "cooperative":
        return "search-badge-cooperative";
      case "notice":
        return "search-badge-notice";
      case "complaint":
        return "search-badge-complaint";
      case "user":
        return "search-badge-user";
      default:
        return "search-badge-page";
    }
  };

  const handleSelectApiItem = (item: FormattedSearchResult) => {
    navigate(item.path, { state: item.state });
    setSearch("");
    setShowDropdown(false);
  };

  const handleSelectStaticItem = (item: ISearchItem) => {
    navigate(item.path, {
      state: {
        sectionId: item.sectionId,
        tab: item.tab,
      },
    });
    setSearch("");
    setShowDropdown(false);
  };

  const hasResults = apiResults.length > 0 || staticResults.length > 0;

  return (
    <>
      <div
        className={`header d-flex align-items-center justify-content-between gap-3`}
      >
        {/* Hamburger / Navigation */}
        <button
          className="btn hamburger-icon p-0"
          onClick={() => setSidebarOpen(true)}
        >
          <i className="fa-solid fa-bars"></i>
        </button>
        {!isDashboard && (
          <span
            className="d-flex align-items-center gap-2 header-back-btn"
            onClick={() => navigate(-1)}
          >
            <i className="fa-solid fa-angle-left"></i>
            {`Back ${!isMobile ? "to Previous Page" : ""}`}
          </span>
        )}

        {/* Search Input Box */}
        <div className="search-box position-relative" ref={searchRef}>
          <div className="position-relative d-flex align-items-center">
            <input
              type="text"
              className="form-control pe-4"
              placeholder="Search cooperatives, notices, complaints, users, pages..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowDropdown(e.target.value.trim().length > 0);
              }}
              onFocus={() => {
                if (search.trim().length > 0) {
                  setShowDropdown(true);
                }
              }}
            />
            {isLoading && (
              <div
                className="position-absolute end-0 me-3 search-spinner"
                title="Searching..."
              ></div>
            )}
          </div>

          {/* Search Dropdown */}
          {showDropdown && search.trim().length > 0 && (
            <div className="search-dropdown">
              {isLoading &&
                apiResults.length === 0 &&
                staticResults.length === 0 && (
                  <div className="search-item text-muted d-flex align-items-center gap-2">
                    <span className="search-spinner"></span> Searching...
                  </div>
                )}

              {/* Database Search Results */}
              {apiResults.length > 0 && (
                <div>
                  <div className="search-dropdown-header">Top Results</div>
                  {apiResults.map((item) => (
                    <div
                      key={item.id}
                      className="search-item d-flex justify-content-between align-items-start gap-2"
                      onClick={() => handleSelectApiItem(item)}
                    >
                      <div className="search-item-content">
                        <div className="search-item-title">{item.title}</div>
                        <small>{item.description}</small>
                      </div>
                      <span
                        className={`search-badge ${getBadgeClass(
                          item.category,
                        )}`}
                      >
                        {item.category}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Navigation Shortcuts */}
              {staticResults.length > 0 && (
                <div>
                  <div className="search-dropdown-header">
                    Navigation Shortcuts
                  </div>
                  {staticResults.slice(0, 8).map((item) => (
                    <div
                      key={`static-${item.id}`}
                      className="search-item d-flex justify-content-between align-items-start gap-2"
                      onClick={() => handleSelectStaticItem(item)}
                    >
                      <div className="search-item-content">
                        <div className="search-item-title">{item.title}</div>
                        <small>{item.description}</small>
                      </div>
                      <span className="search-badge search-badge-page">
                        Page
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {!isLoading && !hasResults && (
                <div className="search-item text-muted">No results found</div>
              )}
            </div>
          )}
        </div>

        {/* Right Side */}
        <div className="d-flex align-items-center gap-3">
          <div className="position-relative d-inline-flex align-items-center">
            <i
              className="fa-solid fa-bell cursor-pointer fs-5"
              onClick={() => setShowNotification(true)}
              title="Notifications"
            ></i>
            {unreadCount > 0 && (
              <span
                className="position-absolute badge rounded-pill bg-danger d-flex align-items-center justify-content-center"
                style={{
                  top: "-6px",
                  right: "-10px",
                  fontSize: "10px",
                  minWidth: "18px",
                  height: "18px",
                  padding: "0 4px",
                  lineHeight: "1",
                  pointerEvents: "none",
                }}
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </div>

          <Link
            to={`/user/view/${user?.user.id}`}
            className="text-decoration-none"
          >
            <img
              src={`${process.env.REACT_APP_Url}/${user?.user.documentUrl}`}
              alt="user"
              className="user-img"
            />
          </Link>
        </div>
      </div>

      <NotificationDrawer
        open={showNotification}
        onClose={() => setShowNotification(false)}
      />
    </>
  );
};

export default SearchHeader;
